import { useState, useEffect, useCallback } from 'react';
import NetworkStatusPopup from '../../Components/NetworkStatusPopup';

const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showPopup, setShowPopup] = useState(false);

  // Function to check actual network connectivity
  const checkNetworkStatus = useCallback(async () => {
    try {
      // Try to fetch a small resource to verify actual connectivity
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      // Try multiple endpoints in case one fails
      const endpoints = [
        'https://www.google.com/favicon.ico',
        'https://www.cloudflare.com/favicon.ico',
        'https://www.apple.com/favicon.ico'
      ];

      let isConnected = false;
      
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            mode: 'no-cors',
            signal: controller.signal,
            cache: 'no-cache'
          });
          // Even with no-cors, if we get here, we have a connection
          isConnected = true;
          break;
        } catch (e) {
          // Try next endpoint
          continue;
        }
      }

      clearTimeout(timeoutId);
      
      // Update state based on connection status
      setIsOnline(isConnected);
      setShowPopup(!isConnected);
      
    } catch (error) {
      // If all fetches fail, we're offline
      setIsOnline(false);
      setShowPopup(true);
    }
  }, []);

  const updateOnlineStatus = useCallback(() => {
    const newStatus = navigator.onLine;
    setIsOnline(newStatus);
    
    if (!newStatus) {
      // If browser says we're offline, show popup immediately
      setShowPopup(true);
    } else {
      // If browser says we're online, verify with actual network check
      checkNetworkStatus();
    }
  }, [checkNetworkStatus]);

  useEffect(() => {
    let mounted = true;

    const checkAndUpdateStatus = async () => {
      if (!mounted) return;
      
      // Initial check
      await checkNetworkStatus();

      // Set up periodic checks every 15 seconds
      const intervalId = setInterval(async () => {
        if (!mounted) return;
        await checkNetworkStatus();
      }, 5 * 60 * 1000); // 5 minutes

      // Listen for browser's online/offline events
      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);

      // Show popup if initially offline
      if (!navigator.onLine) {
        setShowPopup(true);
      }

      return () => {
        mounted = false;
        clearInterval(intervalId);
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
      };
    };

    checkAndUpdateStatus();

    // Cleanup function
    return () => {
      mounted = false;
    };
  }, [checkNetworkStatus, updateOnlineStatus]);

  const NetworkStatusComponent = () => (
    <NetworkStatusPopup 
      open={showPopup} 
      onClose={() => {
        setShowPopup(false);
        // Recheck network status when popup is closed
        checkNetworkStatus();
      }} 
    />
  );

  return {
    isOnline,
    NetworkStatusComponent
  };
};

export default useNetworkStatus;
